import { cn } from "@/lib/class-control";
import { LucideIcon } from "lucide-react";

interface StatsCardProps {
  icon: LucideIcon;
  /** Small category label shown at top (e.g., "Documentos") - only shown in "document" variant */
  categoryLabel?: string;
  /** Main title/label (e.g., "Contratos Vencendo" or "Contratos") */
  label: string;
  /** Main value displayed with icon (e.g., "5" or "Contratos") */
  value: number | string;
  /** Description text (e.g., "387 Arquivos" or "Em progresso no mês") */
  description?: string;
  /** Badge value (e.g., "4.5 GB" or "+10%") */
  badgeValue?: string;
  /** Whether badge should show positive (green) or negative (red) styling */
  badgeVariant?: "positive" | "negative";
  /** Card variant - "default" for dashboard style, "document" for Documentos page style */
  variant?: "default" | "document";
  /** @deprecated Use badgeValue and badgeVariant instead */
  percentage?: number;
}

export default function StatsCard({
  icon: Icon,
  categoryLabel = "Documentos",
  label,
  value,
  description,
  badgeValue,
  badgeVariant = "positive",
  variant = "default",
  percentage
}: StatsCardProps) {
  // Support legacy percentage prop
  const displayBadge = badgeValue || (percentage !== undefined ? `${percentage >= 0 ? '+' : ''}${percentage}%` : undefined);
  const displayVariant = badgeVariant || (percentage !== undefined && percentage >= 0 ? "positive" : "negative");
  const displayDescription = description || (percentage !== undefined ? (percentage >= 0 ? 'Novos esse mês' : 'Esse mês') : undefined);

  // Document variant - Figma style (224px height, shows categoryLabel + title as value)
  if (variant === "document") {
    return (
      <div className="bg-[#15203c] rounded-[20px] px-[30px] py-0 h-[224px] flex flex-col justify-center gap-[46px]">
        {/* Category Label */}
        <p className="text-[12px] text-white font-normal font-['Plus_Jakarta_Sans']">
          {categoryLabel}
        </p>

        {/* Icon + Title */}
        <div className="flex items-center gap-[13px]">
          <Icon className="size-7 text-white" strokeWidth={1.5} />
          <p className="text-[24px] font-bold text-white font-['Plus_Jakarta_Sans']">
            {typeof value === 'string' ? value : label}
          </p>
        </div>

        {/* Description + Badge */}
        <div className="flex items-center justify-between w-full">
          <p className="text-[12px] text-[#888888] font-normal font-['Plus_Jakarta_Sans']">
            {displayDescription}
          </p>
          {displayBadge && (
            <div className="bg-[#0C1427] rounded-[20px] px-[10px] h-[23px] flex items-center justify-center">
              <p className={cn(
                "text-[12px] font-normal font-['Plus_Jakarta_Sans']",
                displayVariant === "positive" ? "text-[#13D480]" : "text-[#DC4426]"
              )}>
                {displayBadge}
              </p>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Default variant - Dashboard style (158px height, shows numeric value)
  return (
    <div className="bg-[#15203c] rounded-[20px] px-[30px] py-5 h-[158px] flex flex-col justify-center gap-[19px]">
      {/* Label */}
      <p className="text-xs text-white">
        {label}
      </p>

      {/* Icon + Value */}
      <div className="flex items-center gap-[13px]">
        <Icon className="size-7 text-white" />
        <p className="text-4xl font-bold text-white">
          {value}
        </p>
      </div>

      {/* Description + Percentage Badge */}
      <div className="flex items-center justify-between gap-4">
        <p className="text-xs text-[#888888]">
          {displayDescription}
        </p>
        {displayBadge && (
          <div className="bg-[#0C1427] rounded-[20px] px-[10px] py-1">
            <p className={cn(
              "text-xs",
              displayVariant === "positive" ? "text-[#13D480]" : "text-[#DC4426]"
            )}>
              {displayBadge}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
